import { gql, NetworkStatus, useQuery } from '@apollo/client'
import { useEffect, useState } from 'react'

import {
  PickupUserListItemFragmentDoc,
  PickupUsersByEventQuery,
  PickupUsersByEventQueryVariables,
  UserPickupTicketStatus,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { useAnalytics } from '@/lib/analytics'

import { Action, State } from './template'

/**
 *
 */
export const useViewModel = makeViewModel<State, Action>(() => {
  const { analytics } = useAnalytics()
  const { router } = useRouter<{ eventId: string }>()
  const [type, setType] = useState<UserPickupTicketStatus>(
    UserPickupTicketStatus.All
  )

  const { data, loading, fetchMore, refetch, networkStatus } = useQuery<
    PickupUsersByEventQuery,
    PickupUsersByEventQueryVariables
  >(QUERY, {
    variables: {
      eventId: router.query.eventId,
      options: {
        type: UserPickupTicketStatus.All,
        isHistory: false,
      },
    },
    notifyOnNetworkStatusChange: true,
  })

  useEffect(() => {
    if (loading || !!data?.pickupUsersByEvent.edges.length) return

    analytics?.track('impression_noData', {
      applicationKey: config.applicationKey,
      artistId:
        data?.pickupUsersByEvent.edges[0]?.node?.eventCollection.collection
          .artistId,
      eventId: router.query.eventId,
      name: 'eventPickup',
    })
  }, [loading, !!data?.pickupUsersByEvent?.edges?.length])

  return {
    state: {
      loading:
        networkStatus === NetworkStatus.loading ||
        networkStatus === NetworkStatus.setVariables,
      refetching: networkStatus === NetworkStatus.fetchMore,
      eventId: router.query.eventId,
      event: data?.pickupUsersByEvent.edges[0]?.node?.event ?? null,
      hasNextPage: !!data?.pickupUsersByEvent.pageInfo.hasNextPage,
      pickupUsers: data?.pickupUsersByEvent,
      isHistory: false,
      type,
    },
    actions: {
      async onFilter(inputType: UserPickupTicketStatus) {
        if (type === inputType) return

        setType(inputType)
        await refetch({
          eventId: router.query.eventId,
          options: {
            type: inputType,
            isHistory: false,
          },
        })
      },
      async onFetchMore() {
        await fetchMore({
          variables: {
            eventId: router.query.eventId,
            options: {
              type,
              isHistory: false,
            },
            pageInput: {
              cursor: data?.pickupUsersByEvent.pageInfo.endCursor,
            },
          },
        })
      },
    },
  }
})

const QUERY = gql`
  query pickupUsersByEvent(
    $eventId: ID!
    $options: PickupUsersByEventOptions!
    $pageInput: PageInput
  ) {
    pickupUsersByEvent(
      eventId: $eventId
      options: $options
      pageInput: $pageInput
    ) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          ...PickupUserListItem
        }
      }
    }
  }
  ${PickupUserListItemFragmentDoc}
`
